import type {
  PendingItemsConsentInput as PendingItemsConsentSchemaInput,
  PendingItemsGuardianDeclaration,
  PendingItemsLinkedItemTarget,
  PendingItemsLinkedPageItem,
  PendingItemsPageItem,
  PendingItemsPageModel,
  PendingItemsPolicySubmitInput as PendingItemsPolicySubmitSchemaInput,
  PendingItemsReceiptDocument as PendingItemsReceiptDocumentSchema,
  PendingItemsReceiptModel as PendingItemsReceiptModelSchema,
  PendingItemsSkinTypeSubmitInput,
  PendingItemsSubmitResult,
} from '@hapana/shared/schemas/policy-compliance/pending-items-public-schemas'

import { extractSubdomain } from '_services/tenant.service'
import type { WireShape } from '_services/wire-shape.types'

export const PENDING_ITEMS_API_BASE = '/api/public/pending-items'
export const PENDING_ITEMS_CSRF_HEADER = 'x-pending-items-request'
export const PENDING_ITEMS_HANDOFF_HEADER = 'x-pending-items-handoff'
const SESSION_WRITE_PATH_PREFIX = '/session/'

export type PendingItemsPublicItem = WireShape<PendingItemsPageItem>
export type PendingItemsLinkedItem = WireShape<PendingItemsLinkedPageItem>
export type PendingItemsPublicModel = WireShape<PendingItemsPageModel>
export type PendingItemsPublicSubmitResult = WireShape<PendingItemsSubmitResult>
export type PendingItemsReceiptDocument = WireShape<PendingItemsReceiptDocumentSchema>
export type PendingItemsReceiptModel = WireShape<PendingItemsReceiptModelSchema>

export type PendingItemsUploadTicket = {
  uploadId: string
  uploadUrl: string
  headers: Array<{ name: string; value: string }>
  expiresAt: string
}

export type PendingItemsGuardianDeclarationInput = PendingItemsGuardianDeclaration
export type PendingItemsPolicySubmitInput = PendingItemsPolicySubmitSchemaInput
export type PendingItemsConsentInput = PendingItemsConsentSchemaInput
export type PendingItemsSkinTypeInput = PendingItemsSkinTypeSubmitInput
export type PendingItemsLinkedTarget = WireShape<PendingItemsLinkedItemTarget>

export class PendingItemsRequestError extends Error {
  status: number
  code: string | null
  details: Record<string, unknown> | null

  constructor(status: number, body: Record<string, unknown> | null) {
    super(typeof body?.code === 'string' ? body.code : `Request failed with status ${status}`)
    this.status = status
    this.code = typeof body?.code === 'string' ? body.code : null
    this.details =
      body?.details && typeof body.details === 'object'
        ? (body.details as Record<string, unknown>)
        : null
  }
}

type RequestInit = { method: 'GET' | 'POST'; body?: unknown; keepalive?: boolean }

const renderedSession: { handoffId: string | null } = { handoffId: null }

const rememberRenderedHandoff = (model: PendingItemsPublicModel): PendingItemsPublicModel => {
  renderedSession.handoffId = model.handoffId
  return model
}

const buildHeaders = (path: string, init: RequestInit): Record<string, string> => ({
  'x-tenant': extractSubdomain() ?? '',
  ...(init.method === 'POST' && { [PENDING_ITEMS_CSRF_HEADER]: '1' }),
  ...(init.method === 'POST' &&
    path.startsWith(SESSION_WRITE_PATH_PREFIX) &&
    renderedSession.handoffId && { [PENDING_ITEMS_HANDOFF_HEADER]: renderedSession.handoffId }),
  ...(init.body !== undefined && { 'Content-Type': 'application/json' }),
})

const sendRequest = async (path: string, init: RequestInit): Promise<Response> => {
  const response = await fetch(`${PENDING_ITEMS_API_BASE}${path}`, {
    method: init.method,
    credentials: 'same-origin',
    referrerPolicy: 'no-referrer',
    cache: 'no-store',
    keepalive: init.keepalive ?? false,
    headers: buildHeaders(path, init),
    ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null
    throw new PendingItemsRequestError(response.status, body)
  }
  return response
}

const requestJson = async <T>(path: string, init: RequestInit = { method: 'GET' }): Promise<T> =>
  (await (await sendRequest(path, init)).json()) as T

export const exchangePendingItemsToken = async (token: string, binding: string | null) =>
  rememberRenderedHandoff(
    await requestJson<PendingItemsPublicModel>('/session', {
      method: 'POST',
      body: binding ? { token, binding } : { token },
    })
  )

export const fetchPendingItemsSession = async () =>
  rememberRenderedHandoff(await requestJson<PendingItemsPublicModel>('/session'))

export const endPendingItemsSession = () =>
  requestJson<{ closed: boolean }>('/session/end', { method: 'POST', body: {}, keepalive: true })

export const verifyPendingItemsIdentity = (input: { submissionId: string; dateOfBirth: string }) =>
  requestJson<PendingItemsPublicSubmitResult>('/session/verify', { method: 'POST', body: input })

export const recordPendingItemsConsent = (input: PendingItemsConsentInput) =>
  requestJson<PendingItemsPublicSubmitResult>('/session/consent', { method: 'POST', body: input })

export const downloadPendingItemsDisclosure = async (): Promise<Blob> =>
  (await sendRequest('/session/disclosure', { method: 'POST', body: {} })).blob()

export const submitPendingItemsPolicy = (input: PendingItemsPolicySubmitInput) =>
  requestJson<PendingItemsPublicSubmitResult>('/session/policies', { method: 'POST', body: input })

export const submitPendingItemsSkinType = (input: PendingItemsSkinTypeInput) =>
  requestJson<PendingItemsPublicSubmitResult>('/session/skin-type', { method: 'POST', body: input })

export const openPendingItemsLinkedItem = (itemKey: string) =>
  requestJson<PendingItemsLinkedTarget>('/session/linked-items/open', {
    method: 'POST',
    body: { itemKey },
  })

export const uploadPendingItemsSignature = async (image: Blob): Promise<string> => {
  const ticket = await requestJson<PendingItemsUploadTicket>('/session/signature-uploads/init', {
    method: 'POST',
    body: {},
  })
  const upload = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: Object.fromEntries(ticket.headers.map((header) => [header.name, header.value])),
    body: image,
  })
  if (!upload.ok) {
    throw new PendingItemsRequestError(upload.status, null)
  }
  await requestJson<{ uploadId: string }>('/session/signature-uploads/finalize', {
    method: 'POST',
    body: { uploadId: ticket.uploadId },
  })
  return ticket.uploadId
}

export const exchangePendingItemsReceiptToken = (token: string) =>
  requestJson<PendingItemsReceiptModel>('/receipt/session', { method: 'POST', body: { token } })

export const fetchPendingItemsReceipt = () =>
  requestJson<PendingItemsReceiptModel>('/receipt/session')

export const fetchPendingItemsReceiptDocumentUrl = (agreementId: string) =>
  requestJson<{ url: string; expiresAt: string }>(
    `/receipt/session/documents/${encodeURIComponent(agreementId)}`
  )
